/**
 * File Management Modals
 * Modal components for file operations (send, rename, delete).
 * 
 * Extracted from App.js for better code organization.
 */

import React, { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Trash2 } from 'lucide-react';

/**
 * Send File Modal Component
 * Allows users to send a file to multiple recipients via email.
 */
export const SendFileModal = ({ file, onSend, onClose }) => {
  const [recipients, setRecipients] = useState(['']);
  const [message, setMessage] = useState('');

  const addRecipient = () => {
    setRecipients([...recipients, '']);
  };

  const updateRecipient = (index, value) => {
    const newRecipients = [...recipients];
    newRecipients[index] = value;
    setRecipients(newRecipients);
  };

  const removeRecipient = (index) => {
    setRecipients(recipients.filter((_, i) => i !== index));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validRecipients = recipients.filter(email => email.trim());
    if (validRecipients.length === 0) {
      toast.error('Please add at least one recipient');
      return;
    }
    onSend(validRecipients, message);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Send File</h3>
        
        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-2">Sending: <span className="font-medium">{file.filename}</span></p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Recipients</label>
            {recipients.map((recipient, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="email"
                  value={recipient}
                  onChange={(e) => updateRecipient(index, e.target.value)}
                  placeholder="Enter email address"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                  required
                  data-testid={`recipient-email-${index}`}
                />
                {recipients.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeRecipient(index)}
                    className="px-2 py-2 text-red-600 hover:bg-red-50 rounded-md"
                    data-testid={`remove-recipient-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addRecipient}
              className="text-sm text-blue-600 hover:text-blue-800"
              data-testid="add-recipient-btn"
            >
              + Add another recipient
            </button>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Message (optional)</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add a message..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              data-testid="send-file-message"
            />
          </div>

          <div className="flex gap-3">
            <Button 
              type="submit" 
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="send-file-submit"
            >
              Send File
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose} 
              className="flex-1"
              data-testid="send-file-cancel"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

/**
 * Rename File Modal Component
 * Allows users to rename a file.
 */
export const RenameFileModal = ({ filename, onFilenameChange, onRename, onClose }) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!filename.trim()) {
      toast.error('Please enter a filename');
      return;
    }
    onRename();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Rename File</h3>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">New filename</label>
            <input
              type="text"
              value={filename}
              onChange={(e) => onFilenameChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              required
              autoFocus
              data-testid="rename-file-input"
            />
          </div>

          <div className="flex gap-3">
            <Button 
              type="submit" 
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="rename-file-submit"
            >
              Rename
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose} 
              className="flex-1"
              data-testid="rename-file-cancel"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

/**
 * Delete File Modal Component
 * Confirmation modal for file deletion.
 */
export const DeleteFileModal = ({ file, onDelete, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete File</h3>
        
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete <span className="font-medium">{file.filename}</span>? 
          This action cannot be undone.
        </p>

        <div className="flex gap-3">
          <Button 
            onClick={onDelete}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            data-testid="delete-file-confirm"
          >
            Delete
          </Button>
          <Button 
            variant="outline" 
            onClick={onClose} 
            className="flex-1"
            data-testid="delete-file-cancel"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default { SendFileModal, RenameFileModal, DeleteFileModal };
